/**
 * PortlessSection — org-wide Portless (Pipe17) fulfilment integration
 * (extracted from the old god component's `renderPortless`).
 *
 * Single write-only Vault key — no enable toggle, since Portless activates
 * per location via the shipping-provider picker. Manifest settings
 * (`PortlessManifestSettingsSection` / `PortlessManifestCard`) render below
 * the credential form, unmodified — they manage their own state/save.
 */
import { useState } from 'react';
import { Truck, Loader2, Send, ShieldCheck, ShieldAlert, KeyRound, X } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { SecretInput } from '@/components/ui/secret-input';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { SettingsSection } from '../components/console/SettingsSection';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';
import { PortlessManifestSettingsSection } from '../components/PortlessManifestSettingsSection';
import { PortlessManifestCard } from '../components/PortlessManifestCard';

export function PortlessSection() {
  const settingsQuery = trpc.productionSettings.getPortlessSettings.useQuery();
  const updateMutation = trpc.productionSettings.updatePortlessSettings.useMutation();
  const testMutation = trpc.productionSettings.testPortless.useMutation();

  const [apiKey, setApiKey] = useState('');
  const [isEditingCredentials, setIsEditingCredentials] = useState(false);

  const isDirty = apiKey !== '';

  useDirtyRegistration(
    'portless',
    isDirty
      ? {
          label: 'Portless',
          save: async () => {
            await updateMutation.mutateAsync({ portlessApiKey: apiKey || null });
            setApiKey('');
            setIsEditingCredentials(false);
            await settingsQuery.refetch();
          },
          discard: () => {
            setApiKey('');
            setIsEditingCredentials(false);
          },
        }
      : null,
  );

  const handleTest = () => {
    testMutation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success('Connection successful', { description: 'Portless (Pipe17) API key is valid' });
        } else {
          const errorResult = result as { success: false; error: string };
          toast.error('Connection failed', { description: errorResult.error || 'Invalid API key' });
        }
      },
      onError: (error) => toast.error('Test failed', { description: error.message }),
    });
  };

  const hasCredentials = settingsQuery.data?.hasCredentials ?? false;

  return (
    <SettingsSection
      id="portless"
      title="Portless"
      description="Pipe17 API key and manifest settings for Portless fulfilment."
      icon={Truck}
      scope="org"
    >
      {settingsQuery.isPending ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {hasCredentials ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <ShieldCheck className="h-5 w-5 text-green-600 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-800">API key saved</p>
                <p className="text-xs text-green-700/80">The key is stored securely in Vault.</p>
              </div>
              {!isEditingCredentials && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2 border-green-300 bg-white/70 text-green-800 hover:bg-white"
                  onClick={() => setIsEditingCredentials(true)}
                  aria-controls="portless-credential-editor"
                  aria-expanded={false}
                >
                  <KeyRound className="h-4 w-4" aria-hidden="true" />
                  Replace API key
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-800">No API key configured</p>
                <p className="text-xs text-amber-700/80">Get your Pipe17 API key from Portless</p>
              </div>
              {!isEditingCredentials && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 gap-2 border-amber-300 bg-white/70 text-amber-900 hover:bg-white"
                  onClick={() => setIsEditingCredentials(true)}
                  aria-controls="portless-credential-editor"
                  aria-expanded={false}
                >
                  <KeyRound className="h-4 w-4" aria-hidden="true" />
                  Add API key
                </Button>
              )}
            </div>
          )}

          <p className="text-sm text-muted-foreground/90 leading-relaxed">
            Portless generates the first-mile (cross-dock) label when it pulls the order from Shopify. At
            packing, the IMS fetches and prints that label; Portless re-labels and ships to the customer.
            Assign Portless to a location under Locations &gt; Shipping provider.
          </p>

          {isEditingCredentials && (
            <div id="portless-credential-editor" className="space-y-4 rounded-lg border bg-muted/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{hasCredentials ? 'Replace API key' : 'Add API key'}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">The existing value cannot be viewed.</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-10 gap-2"
                  onClick={() => {
                    setApiKey('');
                    setIsEditingCredentials(false);
                  }}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  Cancel
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="portless-api-key" className="text-sm font-medium">
                  API Key
                </Label>
                <SecretInput
                  id="portless-api-key"
                  name="portless-api-key-replacement"
                  placeholder="Enter Pipe17 API key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="h-10 font-mono text-sm"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={!hasCredentials || testMutation.isPending}
              className="h-10 min-w-[120px] gap-2"
            >
              {testMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Test Connection
            </Button>
          </div>

          <PortlessManifestSettingsSection />
          <PortlessManifestCard />
        </div>
      )}
    </SettingsSection>
  );
}
